import { api, getAccessToken } from '@/lib/api/client';
import {
  cloneCompetitionApi,
  createDraftCompetitionApi,
  getCompetitionApi,
  getCompetitionResultsApi,
  getCompetitionsApi,
  publishCompetitionApi,
} from '@api-client/competitions';
import { uploadFileApi } from '@api-client/files';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import type { CompetitionRule } from '@shared/types/entities';

export {
  cloneCompetitionApi,
  createDraftCompetitionApi,
  getCompetitionApi,
  getCompetitionResultsApi,
  getCompetitionsApi,
  publishCompetitionApi,
};
export type {
  Competition,
  CompetitionResult,
  CreateCompetitionDraftPayload,
  CreateCompetitionPayload,
  GetCompetitionsParams,
  PublishCompetitionErrorBody,
  UpdateCompetitionPayload,
} from '@api-client/competitions';

export async function createCompetitionApi(
  data: import('@api-client/competitions').CreateCompetitionPayload,
): Promise<import('@shared/types/entities').Competition> {
  return api.post('/competitions', data);
}

export async function updateCompetitionApi(
  id: number,
  data: import('@api-client/competitions').UpdateCompetitionPayload,
): Promise<import('@shared/types/entities').Competition> {
  return api.patch(`/competitions/${id}`, data);
}

export async function updateCompetitionStatusApi(
  id: number,
  status: CompetitionStatus,
): Promise<import('@shared/types/entities').Competition> {
  return api.patch(`/competitions/${id}/status`, { status });
}

export async function deleteCompetitionApi(id: number): Promise<void> {
  return api.delete(`/competitions/${id}`);
}

/** Désactive un concours (super-admin uniquement, irréversible). */
export async function disableCompetitionApi(id: number): Promise<import('@shared/types/entities').Competition> {
  return api.patch(`/competitions/${id}/disable`);
}

export async function enableCompetitionApi(id: number): Promise<import('@shared/types/entities').Competition> {
  return api.patch(`/competitions/${id}/enable`);
}

export async function uploadCompetitionCoverApi(competitionId: number, file: File): Promise<void> {
  await uploadFileApi({
    usage: 'COMPETITION_COVER',
    file,
    extraFields: { competitionId: String(competitionId) },
  });
}

/** Récupère toutes les compétitions d'une organisation en paginant automatiquement (limit max = 100). */
export async function fetchAllCompetitionsForOrg(
  organizationId: number,
): Promise<import('@shared/types/entities').Competition[]> {
  const limit = 100;
  let page = 1;
  const all: import('@shared/types/entities').Competition[] = [];
  while (true) {
    const res = await getCompetitionsApi({ organizationId, limit, page });
    all.push(...res.data);
    if (page >= res.meta.totalPages) break;
    page++;
  }
  return all;
}

// ─── Règles ───────────────────────────────────────────────

export interface UpsertRulePayload {
  id?: number;
  content: string;
  type: CompetitionRule['type'];
  isVisible: boolean;
  order: number;
}

export async function getCompetitionRulesApi(id: number): Promise<CompetitionRule[]> {
  return api.get(`/competitions/${id}/rules`);
}

export async function upsertCompetitionRuleApi(id: number, data: UpsertRulePayload): Promise<CompetitionRule> {
  return api.post(`/competitions/${id}/rules`, data);
}

export async function deleteCompetitionRuleApi(ruleId: number): Promise<void> {
  return api.delete(`/competitions/rules/${ruleId}`);
}

// ─── Résultats ────────────────────────────────────────────

export interface UpsertResultsPayload {
  results: Array<{ teamId: number; rank: number; points?: number; notes?: string }>;
}

export async function upsertCompetitionResultsApi(
  id: number,
  data: UpsertResultsPayload,
): Promise<import('@shared/types/entities').CompetitionResult[]> {
  return api.post(`/competitions/${id}/results`, data);
}

// ─── CSV exports ─────────────────────────────────────────

async function downloadCsv(endpoint: string, filename: string): Promise<void> {
  const base = (import.meta.env.VITE_URL_API as string)?.replace(/\/$/, '') || 'http://localhost:3230';
  const token = getAccessToken();
  const response = await fetch(`${base}/v1${endpoint}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Erreur ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface RegistrationsExportFilters {
  registrationStatus?: string[];
  paymentStatus?: string[];
  /** GV-04 : clés canoniques des colonnes CSV (undefined = toutes). */
  fields?: string[];
  /** ISO date (YYYY-MM-DD) — borne basse inclusive sur la date d'inscription. */
  dateFrom?: string;
  /** ISO date (YYYY-MM-DD) — borne haute inclusive sur la date d'inscription. */
  dateTo?: string;
}

export async function exportCompetitionRegistrationsCsv(
  id: number,
  alias: string,
  filters: RegistrationsExportFilters = {},
): Promise<void> {
  // L'API refuse fields= ; ne jamais transformer une sélection vide en export complet.
  if (filters.fields && filters.fields.length === 0) {
    throw new Error('Sélectionnez au moins une colonne à exporter');
  }
  const params = new URLSearchParams();
  if (filters.fields) params.set('fields', filters.fields.join(','));
  if (filters.registrationStatus?.length) params.set('registrationStatus', filters.registrationStatus.join(','));
  if (filters.paymentStatus?.length) params.set('paymentStatus', filters.paymentStatus.join(','));
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  const qs = params.toString();
  const date = new Date().toISOString().split('T')[0];
  return downloadCsv(
    `/competitions/${id}/export/registrations${qs ? `?${qs}` : ''}`,
    `inscriptions-${alias}-${date}.csv`,
  );
}

// ─── XLSX exports ────────────────────────────────────────
// NOTE: endpoint registrations.xlsx à ajouter côté API avant d'activer cette fonction.
// export async function exportCompetitionRegistrationsXlsx(id: number, alias: string): Promise<void> {
//   const date = new Date().toISOString().split('T')[0];
//   const base = (import.meta.env.VITE_URL_API as string)?.replace(/\/$/, '') || 'http://localhost:3230';
//   const token = getAccessToken();
//   const response = await fetch(`${base}/v1/competitions/${id}/export/registrations.xlsx`, {
//     method: 'GET',
//     headers: token ? { Authorization: `Bearer ${token}` } : {},
//     credentials: 'include',
//   });
//   if (!response.ok) throw new Error(`Erreur ${response.status}`);
//   const blob = await response.blob();
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement('a');
//   a.href = url;
//   a.download = `inscriptions-${alias}-${date}.xlsx`;
//   document.body.appendChild(a);
//   a.click();
//   document.body.removeChild(a);
//   URL.revokeObjectURL(url);
// }

export async function exportCompetitionResultsCsv(id: number, alias: string): Promise<void> {
  const date = new Date().toISOString().split('T')[0];
  return downloadCsv(`/competitions/${id}/export/results`, `resultats-${alias}-${date}.csv`);
}

export async function exportCompetitionFfpjpXlsx(id: number, alias: string): Promise<void> {
  const date = new Date().toISOString().split('T')[0];
  const base = (import.meta.env.VITE_URL_API as string)?.replace(/\/$/, '') || 'http://localhost:3230';
  const token = getAccessToken();
  const response = await fetch(`${base}/v1/competitions/${id}/export/ffpjp`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Erreur ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${alias}_FFPJP_${date}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Gestion Concours — Word results import (GV-01) ─────────

export interface GestionConcoursDocxPreview {
  previewId: string;
  totalMatches: number;
  matchedTeams: number;
  totalTeams: number;
  eligible: boolean;
  unmatched: Array<{ players: string[]; round: string; reason: 'NOT_FOUND' | 'AMBIGUOUS' }>;
  issues: Array<{ round: string; number: number; message: string }>;
  firstImport: boolean;
  fileChanged: boolean;
  formatChanged: boolean;
  unchangedDocument: boolean;
  documentChanges: null | {
    added: Array<{ round: string; number: number }>;
    removed: Array<{ round: string; number: number }>;
    changed: Array<{ round: string; number: number }>;
  };
  rankChanges: Array<{ teamId: number; name: string; previousRank: number | null; newRank: number }>;
  totalRankChanges: number;
}

export function previewGestionConcoursDocxApi(competitionId: number, file: File): Promise<GestionConcoursDocxPreview> {
  const data = new FormData();
  data.append('file', file, file.name);
  return api.postMultipart<GestionConcoursDocxPreview>(`/competitions/${competitionId}/results/docx/preview`, data);
}

export function applyGestionConcoursDocxApi(
  competitionId: number,
  previewId: string,
): Promise<{ saved: number; message: string }> {
  return api.post<{ saved: number; message: string }>(`/competitions/${competitionId}/results/docx/apply`, {
    previewId,
  });
}

export function sendGestionConcoursFormatReportApi(
  competitionId: number,
  previewId: string,
): Promise<{ sent: boolean; message: string }> {
  return api.post<{ sent: boolean; message: string }>(`/competitions/${competitionId}/results/docx/feedback`, {
    previewId,
  });
}
